import React, { useCallback, useEffect, useState } from 'react';
import { Play, Download, Loader2, Check, X } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import NoDataFound from '../../common/NoDataFound';
import Skeleton from '../../common/Skeleton';
import Toast, { ToastMessage } from '../../common/Toast';
import FileAnalyticsAPI from '../fileAnalyticsService';
import { Rule } from '../types';

/**
 * Data Analytics "Run rules" popup. Lists every rule in the selected pipeline and
 * runs them — sequentially on "Run all" so per-rule progress is visible — then lets
 * you download each run's matched-rows output. Running lives in Data Analytics (the
 * run persona); GRC only defines rules. Colour is used only for a genuine run
 * failure (real inline feedback), never for static status labels.
 */
interface RuleRunModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionTypeId?: string;
  pipelineName?: string;
}

type RunState = 'idle' | 'running' | 'done' | 'failed';

interface RowState {
  state: RunState;
  runId?: string;
  rowsMatched?: number;
  error?: string;
}

const RuleRunModal: React.FC<RuleRunModalProps> = ({ isOpen, onClose, collectionTypeId, pipelineName }) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [runStates, setRunStates] = useState<Record<string, RowState>>({});
  const [runningAll, setRunningAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await FileAnalyticsAPI.listRules(collectionTypeId);
      setRules(list);
      setRunStates({});
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to load rules' });
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [collectionTypeId]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const runOne = async (rule: Rule): Promise<void> => {
    setRunStates(prev => ({ ...prev, [rule.id]: { state: 'running' } }));
    try {
      const res = await FileAnalyticsAPI.runRule(rule.id);
      setRunStates(prev => ({
        ...prev,
        [rule.id]: {
          state: 'done',
          runId: res?.run_id != null ? String(res.run_id) : undefined,
          rowsMatched: res?.rows_matched,
        },
      }));
    } catch (err: any) {
      setRunStates(prev => ({
        ...prev,
        [rule.id]: { state: 'failed', error: err?.response?.data?.detail || 'Run failed' },
      }));
    }
  };

  const runAll = async () => {
    setRunningAll(true);
    // Sequential so each rule's progress is visible and we don't hammer the engine.
    for (const r of rules) {
      // eslint-disable-next-line no-await-in-loop
      await runOne(r);
    }
    setRunningAll(false);
  };

  const handleDownload = async (runId: string) => {
    setDownloadingId(runId);
    try {
      const url = await FileAnalyticsAPI.downloadRun(runId);
      if (url) window.open(url, '_blank', 'noopener');
      else setToast({ type: 'error', message: 'No download available for this run' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Download failed' });
    } finally {
      setDownloadingId(null);
    }
  };

  const doneCount = Object.values(runStates).filter(s => s.state === 'done').length;
  const busy = runningAll || Object.values(runStates).some(s => s.state === 'running');

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} size="lg">
      <Modal.Header>{pipelineName ? `Run rules — ${pipelineName}` : 'Run rules'}</Modal.Header>
      <Modal.Body>
        <Toast toast={toast} onClose={() => setToast(null)} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }} data-testid="cm-run-summary">
            {loading ? 'Loading rules…' : `${rules.length} rule${rules.length === 1 ? '' : 's'} · ${doneCount} run`}
          </span>
          <Button
            variant="fill"
            icon={Play}
            onClick={runAll}
            disabled={loading || busy || rules.length === 0}
            data-testid="BTN_cm_run_all"
          >
            {runningAll ? 'Running…' : 'Run all'}
          </Button>
        </div>

        {loading ? (
          <div className="table-list">
            <table className="table-list-table">
              <tbody><Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', { width: 'icon', align: 'right' }]} /></tbody>
            </table>
          </div>
        ) : rules.length === 0 ? (
          <NoDataFound title="No rules" message="This pipeline has no rules yet. Define one in GRC › Continuous Monitoring › Rules." />
        ) : (
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Severity</th>
                    <th className="table-list-th">Progress</th>
                    <th className="table-list-th table-list-th--right">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => {
                    const st: RowState = runStates[r.id] ?? { state: 'idle' };
                    return (
                      <tr key={r.id} className="table-list-row" data-testid={`cm-run-row-${r.id}`}>
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{r.name}</span>
                          {r.description && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.description}</div>}
                        </td>
                        <td className="table-list-cell" style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{r.severity}</td>
                        <td className="table-list-cell" style={{ fontSize: 12 }} data-testid={`cm-run-progress-${r.id}`}>
                          {st.state === 'idle' && <span style={{ color: 'var(--text-tertiary)' }}>Not run</span>}
                          {st.state === 'running' && (
                            <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Loader2 size={13} className="spin" /> Running…
                            </span>
                          )}
                          {st.state === 'done' && (
                            <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Check size={13} /> {st.rowsMatched != null ? `${st.rowsMatched.toLocaleString()} matched` : 'Completed'}
                            </span>
                          )}
                          {st.state === 'failed' && (
                            <span style={{ color: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: 6 }} title={st.error}>
                              <X size={13} /> Failed
                            </span>
                          )}
                        </td>
                        <td className="table-list-cell table-list-cell--actions">
                          <Button
                            variant="secondary-outline"
                            icon={Play}
                            onClick={() => runOne(r)}
                            disabled={busy}
                            data-testid={`BTN_cm_run_one_${r.id}`}
                            title="Run this rule"
                          >
                            Run
                          </Button>
                          {st.state === 'done' && st.runId && (
                            <Button
                              variant="outline"
                              icon={Download}
                              onClick={() => handleDownload(st.runId!)}
                              disabled={downloadingId === st.runId}
                              data-testid={`BTN_cm_run_download_${r.id}`}
                              title="Download matched rows"
                            >
                              {downloadingId === st.runId ? 'Downloading…' : 'Download'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose} disabled={busy} data-testid="BTN_cm_run_close">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RuleRunModal;
